import React, { useEffect, useState } from 'react';
import { useSelector, connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import Button from 'yoda-core-components/lib/components/Button/Button';
import Icon from 'yoda-core-components/lib/components/Icon/Icon';
import { dt } from 'yoda-core-components/lib/helpers/Utils/GetTailwindToken';
import { Tabs, TabPanel } from 'yoda-core-components/lib/components/Tabs/Tabs';
import Title from 'yoda-core-components/lib/components/Tabs/Title';
import Content from 'yoda-core-components/lib/components/Tabs/Content';
import { selectProductDetailsWrapper } from 'yoda-site-components/lib/selectors/PdpNameSpaceSelector';
import { selectFeatureFlags } from 'yoda-site-components/lib/selectors/ContextSelector';
import selectProductType from 'yoda-site-components/lib/selectors/productDetails/selectProductType';
import { templateTypes } from 'yoda-site-components/lib/selectors/productDetails/selectProductType.config';
import {
    selectedProductSkus,
    initProductOptions,
} from 'yoda-site-components/lib/selectors/ProductOptionsSelector';
import { selectMarketPlaceSellerInfo } from 'yoda-site-components/lib/selectors/MarketPlaceSelector';
import SizeGuide from 'yoda-site-components/lib/components/SizeGuide/SizeGuide';
import KnowledgeAssistance from 'yoda-site-components/lib/components/KnowledgeAssistance/KnowledgeAssistance';
import StaticRenderer from 'yoda-site-components/lib/components/Renderers/StaticRenderer';
import SlidePanel from 'yoda-site-components/lib/components/SlidePanel/SlidePanel';
import ErrorBoundary from 'yoda-site-components/lib/components/ErrorBoundary/ErrorBoundary';
import ChevronLeft from 'yoda-core-components/lib/assets/svg/ChevronLeft.svg';
import ChevronRight from 'yoda-core-components/lib/assets/svg/ChevronRight.svg';
import analyticsAction from 'yoda-site-components/lib/actions/AnalyticsAction';
import ShippingSection from '../ShippingAndReturns/ShippingAndReturns';
import GeneralInformation from '../GeneralInformation/GeneralInformation';
import ProductInfoSelector from '../../selectors/ProductInfo/ProductInfoSelector';
import BundleProductInfoSelector from '../../selectors/Bundle/BundleProductInfoSelector';
import ProductDescription from '../ProductDescription/ProductDescription';
import SellerInformation from '../SellerInformation/SellerInformation';
import ProductConstants from '../../helpers/ProductConstants';

const getProductDescription = (content, enableSlider, enableMobileSliderPD) => {
    return (
        <ProductDescription
            data-automation-id="at-section-prodDescription"
            productDetails={content}
            enableSlider={enableSlider}
            enableMobileSliderPD={enableMobileSliderPD}
        />
    );
};

/* istanbul ignore next */
const isMobileSliderPDElementExists = () => {
    const { fromMobileSliderPD } = this.props;
    if (fromMobileSliderPD) {
        const panelContentElement = document.querySelector(
            'div[data-automation-id="mobileSliderPD-at-panel-content"]'
        );
        const activepanelElement = document.getElementById('mobileSliderPD-at-active-panel');
        if (panelContentElement && activepanelElement) {
            return true;
        }
    }
    return false;
};
const getShippingAndReturns = (content, generalInfoData) => {
    return (
        <ShippingSection
            dataAutomationId="shippingSection"
            productDetails={content}
            generalInfo={generalInfoData > 0}
            isMobileSliderPDExists={isMobileSliderPDElementExists}
        />
    );
};

const getGeneralInfo = (content) => {
    if (content && content.length > 0) {
        return (
            <div className={dt(['pt-1', 'pb-3'])}>
                <div
                    className={dt([
                        'font-open-sans-semibold',
                        'text-lg',
                        'leading-7',
                        'text-black',
                        'pb-1',
                    ])}
                >
                    General Information
                </div>
                <GeneralInformation
                    dataAutomationId="generalInformation"
                    generalInfo={content}
                    isMobileSliderPDExists={isMobileSliderPDElementExists}
                />
            </div>
        );
    }
    return null;
};

const renderShippingSection = (content, enableMobileSliderPD) => {
    return (
        <>
            {getShippingAndReturns(
                get(content, '[0].content'),
                get(content, '[1].content').length,
                enableMobileSliderPD
            )}
            {getGeneralInfo(get(content, '[1].content'))}
        </>
    );
};

const renderKA = (shouldRenderKA, productDetails, pdpProductType) => {
    return shouldRenderKA ? (
        <KnowledgeAssistance
            dataAutomationId="at-knowledge-assistance"
            placement="bottom"
            productDetails={productDetails}
            templateType={pdpProductType?.templateType}
        />
    ) : null;
};

const getComponent = (item) => {
    switch (item.type) {
        case 'productDescription':
            return getProductDescription(item.content, item.enableSlider, false);
        case 'shippingAndReturns':
            return renderShippingSection(item.content);
        case ProductConstants.SELLER_INFORMATION_TYPE:
            return <SellerInformation showHeading={false} />;
        /* istanbul ignore next */
        default:
            return '';
    }
};

const getMobileSliderComponent = (itemContent, itemType, itemEnbleSlider, enableMobileSliderPD) => {
    switch (itemType) {
        case 'productDescription':
            return getProductDescription(itemContent, itemEnbleSlider, enableMobileSliderPD);
        case 'shippingAndReturns':
            return renderShippingSection(itemContent, enableMobileSliderPD);
        case ProductConstants.SELLER_INFORMATION_TYPE:
            return <SellerInformation showHeading={false} />;
        default:
            return '';
    }
};

const renderHrDesc = () => {
    return (
        <hr
            className={dt([
                'my-0',
                'border-solid',
                'border-t',
                'border-b-0',
                'border-l-0',
                'border-r-0',
                'border-gray-15',
            ])}
        />
    );
};

const DescriptionAndShipping = ({ actions }) => {
    const [isSlidePanelOpen, setSlidePanelOpen] = useState(false);
    const [descriptionSlider, setDescription] = useState({
        title: '',
        content: '',
        type: '',
        enableSlider: false,
    });
    const pdpProductType = useSelector((state) => selectProductType(false)(state));
    const isBundle = pdpProductType?.templateType === templateTypes.BUNDLE;
    const productDetails = isBundle
        ? useSelector((state) => BundleProductInfoSelector(state))
        : useSelector((state) => selectProductDetailsWrapper(false)(state));
    const infoDetails = useSelector((state) => ProductInfoSelector(state));
    const featureFlags = useSelector((state) => selectFeatureFlags(state));
    const selectedProductLot = useSelector((state) => selectedProductSkus(false)(state));
    const marketPlaceInfo = useSelector((state) => selectMarketPlaceSellerInfo(false)(state)) || {};
    const productOptions = useSelector((state) => initProductOptions(false)(state)) || {};
    const isMarketplace = featureFlags.enableMarketPlace && marketPlaceInfo.isMarketPlaceProduct;
    const { enableMarketplaceSellerDetails = false } = featureFlags;
    const showSellerDetails = isMarketplace && enableMarketplaceSellerDetails;
    const filteredItems = productOptions.filteredItems || [];
    const [shouldRenderTabs, setShouldRenderTabs] = useState(true);
    const validKAsWithDataDrivenSizeChart =
        !isEmpty(get(productDetails, 'kaWithSizeGuide.knowledgeAssistants', {})) ||
        !isEmpty(get(productDetails, 'kaWithSizeGuide.sizeGuide', {}));
    const KnowledgeAssistantsCount = infoDetails.KAData;

    let shouldRenderKA = false;
    const lotDescription =
        selectedProductLot.length &&
        selectedProductLot[0].description &&
        selectedProductLot[0].description.length
            ? selectedProductLot[0].description
            : productDetails.description;
    const skuDescription =
        isMarketplace && filteredItems.length === 1 && productOptions.pricingURL?.itemOfferingCall
            ? get(filteredItems, '[0].description', null)
            : null;
    const productDescription = skuDescription || lotDescription;
    const isEmptyDescription = isBundle ? isEmpty(productDescription) : false;
    if (!get(featureFlags, 'disableDataSizeChart', false) && isBundle) {
        shouldRenderKA = validKAsWithDataDrivenSizeChart;
    } else {
        shouldRenderKA = KnowledgeAssistantsCount > 0;
    }
    const handleResize = () => {
        setShouldRenderTabs(window.innerWidth < 768 && !isEmptyDescription);
    };

    const onBackButtonClick = () => {
        setSlidePanelOpen(false);
    };
    /* istanbul ignore next */
    const handleDescriptionDetails = (items) => {
        // We'll replace this in the upcoming sprints with the common analytics tracking modular action
        actions.triggerPlccAnalytics({
            name: `${items?.name} Tab`,
            linktext: items?.name,
            productid: productDetails?.id,
        });
        setSlidePanelOpen(true);
        setDescription((prevState) => ({
            ...prevState,
            title: items.sliderTitle,
        }));
        setDescription((prevState) => ({
            ...prevState,
            content: items.content,
        }));
        setDescription((prevState) => ({
            ...prevState,
            type: items.type,
        }));
        setDescription((prevState) => ({
            ...prevState,
            enableSlider: items.enableSlider,
        }));
    };

    useEffect(() => {
        /* istanbul ignore next */
        if (!__SERVER__) {
            window.addEventListener('resize', () => {
                handleResize();
            });
            handleResize();
        }
        return () => {
            !__SERVER__ &&
                window.removeEventListener('resize', () => {
                    handleResize();
                });
        };
    }, []);
    const sizeGuideKA = productDetails?.sizeGuide;
    const { title, type, content, enableSlider } = descriptionSlider;
    if (shouldRenderTabs) {
        const tabTitleTxt = [
            'text-base',
            'font-open-sans-semibold',
            'leading-6',
            'text-center',
            'text-black',
        ];
        const inActiveBorder = ['border-b-2', 'border-gray-15', 'border-solid'];
        const activeBorder = ['sm:!border-b-4', 'sm:!border-brand-red', 'sm:!border-solid'];
        const myTheme = {
            tabActiveClass: dt([...activeBorder]),
            tabButtonClass: dt(['m-auto']),
            tabContainerClass: dt(['flex', 'h-11', 'smOnly:-mx-6', 'w-[calc(100%+48px)]']),
            tabListItemClass: dt(['w-1/2', 'flex', ...inActiveBorder, ...tabTitleTxt]),
            tabPanelTitleClass: '',
            tabPanelContentClass: dt(['py-4']),
        };
        const shippingSection = [
            {
                name: 'shippingAndReturns',
                content: productDetails,
            },
            {
                name: 'generalInfo',
                content: infoDetails?.generalInfoData,
            },
        ];
        const mobilePDSliderItems = [];
        !isEmptyDescription &&
            productDetails &&
            mobilePDSliderItems.push({
                name: 'Product Details',
                content: productDetails,
                type: 'productDescription',
                enableSlider: shouldRenderTabs,
                sliderTitle: 'Product Details',
            });
        sizeGuideKA &&
            mobilePDSliderItems.push({
                name: 'Size Chart',
                type: 'sizeChart',
                sliderTitle: 'Size Chart',
            });
        shippingSection &&
            mobilePDSliderItems.push({
                name: 'Shipping & Returns',
                content: shippingSection,
                type: 'shippingAndReturns',
                sliderTitle: 'Shipping & Returns',
            });
        showSellerDetails &&
            mobilePDSliderItems.push({
                name: ProductConstants.SELLER_INFORMATION_LABEL,
                type: ProductConstants.SELLER_INFORMATION_TYPE,
                sliderTitle: ProductConstants.SELLER_INFORMATION_HEADING,
            });
        const items = [];
        !isEmptyDescription &&
            items.push({
                name: 'Description',
                content: productDetails,
                type: 'productDescription',
                enableSlider: shouldRenderTabs,
            });
        items.push({
            name: 'Shipping & Returns',
            content: shippingSection,
            type: 'shippingAndReturns',
        });
        if (showSellerDetails) {
            items.push({
                name: ProductConstants.SELLER_INFORMATION_LABEL,
                type: ProductConstants.SELLER_INFORMATION_TYPE,
            });
        }
        return (
            <div id="descriptionAndShippingContainer">
                {featureFlags?.enableMobileSliderPD ? (
                    <>
                        <div className={`${dt(['smOnly:-mx-6', 'smOnly:pb-5'])}`}>
                            {mobilePDSliderItems &&
                                mobilePDSliderItems.map((items, i) => (
                                    <>
                                        {renderHrDesc()}
                                        <Button
                                            className={dt([
                                                'flex',
                                                'items-center',
                                                '!py-5',
                                                '!px-5',
                                                '!w-full',
                                            ])}
                                            onClick={() => handleDescriptionDetails(items)}
                                            buttonType="Text"
                                        >
                                            <span
                                                className={dt([
                                                    'font-open-sans-semibold',
                                                    'text-lg',
                                                    'flex',
                                                    'items-center',
                                                    'capitalize',
                                                ])}
                                            >
                                                {items.name}
                                            </span>
                                            <Icon
                                                className={dt(['ml-auto'])}
                                                automationId="at-ydt-bopis-store-icon"
                                                iconType="svg"
                                                width="28px"
                                                height="28px"
                                                viewBox="0 0 32 32"
                                            >
                                                <ChevronRight />
                                            </Icon>
                                        </Button>
                                    </>
                                ))}
                            {renderHrDesc()}
                        </div>
                        {isSlidePanelOpen && title && (
                            <ErrorBoundary>
                                <SlidePanel
                                    isOpenPanel={isSlidePanelOpen}
                                    onClosePanel={onBackButtonClick}
                                    panelTitle={title}
                                    panelTitleAlignment="left"
                                    panelIcon="close"
                                    panelIconComponent={<ChevronLeft />}
                                    fromMobileSliderPD={featureFlags?.enableMobileSliderPD}
                                >
                                    <div className={dt(['px-6', 'py-6'])}>
                                        {getMobileSliderComponent(
                                            content,
                                            type,
                                            enableSlider,
                                            featureFlags?.enableMobileSliderPD
                                        )}
                                        {type === 'sizeChart' && sizeGuideKA && (
                                            <>
                                                <div
                                                    className={dt([
                                                        'text-base',
                                                        'font-bold',
                                                        'pb-2',
                                                    ])}
                                                >
                                                    {productDetails?.sizeGuide?.name}
                                                </div>
                                                <SizeGuide sizeGuideOptions={sizeGuideKA} />
                                            </>
                                        )}
                                    </div>
                                </SlidePanel>
                            </ErrorBoundary>
                        )}{' '}
                    </>
                ) : (
                    <Tabs
                        activeTabPanelIndex={0}
                        data-automation-id="tabbed-description-section"
                        themeConfig={myTheme}
                        key={`descriptionAndShipping-${items.length}`}
                    >
                        {items.map((item, i) => (
                            <TabPanel title={item.name} index={i} key={item.name}>
                                <Title>{item.name}</Title>
                                <StaticRenderer>
                                    <Content>{getComponent(item)}</Content>
                                </StaticRenderer>
                            </TabPanel>
                        ))}
                    </Tabs>
                )}
                {renderKA(shouldRenderKA, productDetails, pdpProductType)}
            </div>
        );
    }
    const gridSpacing = {};
    /* istanbul ignore else */
    if (!shouldRenderTabs && !isEmptyDescription) {
        gridSpacing.col1 = [
            'lg:w-1/12',
            'lg:inline-block',
            'scale-100',
            'align-top',
            'smMd:hidden',
        ];
        gridSpacing.col10 = ['xl10', 'lg:w-10/12', 'mdOnly:w-full', 'lg:mx-auto', 'md12'];
        gridSpacing.col6 = ['col6'];
    } else {
        gridSpacing.col1 = [''];
        gridSpacing.col10 = [''];
        gridSpacing.col6 = [''];
    }
    const descriptionTitleStyle = [
        'font-open-sans-semibold',
        'lg:text-xlarge',
        'mdOnly:text-lg',
        'lg:leading-8',
        'mdOnly:leading-7',
        'text-black',
    ];
    const titleStyleEmptyDescription = isEmptyDescription
        ? [...descriptionTitleStyle, 'smMd:text-center']
        : [...descriptionTitleStyle];
    const shippingAndReturnEmptyDescription = isEmptyDescription
        ? ['lg:w-5/12', 'mdOnly:w-full', 'lg:px-2']
        : ['md:!pl-2'];
    return (
        <>
            <div className={dt([...gridSpacing?.col1])} />
            <div
                id="productDescriptionParent"
                className={dt(['flex', 'sm:!p-0', ...gridSpacing?.col10])}
            >
                {!isEmptyDescription ? (
                    <div
                        className={dt(['sm:!pl-0', 'md:!pr-2', ...gridSpacing?.col6])}
                        aria-label="productDeccription"
                    >
                        <div className={dt([...descriptionTitleStyle, 'pb-3'])}>
                            Product Details
                        </div>
                        {getProductDescription(productDetails, false, false)}
                    </div>
                ) : (
                    <div className={dt(['lg:w-7/12'])} />
                )}
                <div
                    className={dt([
                        'sm:!pr-0',
                        ...shippingAndReturnEmptyDescription,
                        ...gridSpacing?.col6,
                    ])}
                >
                    <div aria-label="shippingAndReturn">
                        <div className={dt([...titleStyleEmptyDescription, 'pb-3'])}>
                            Shipping &amp; Returns
                        </div>
                        {getShippingAndReturns(productDetails)}
                        {getGeneralInfo(infoDetails?.generalInfoData)}
                        {showSellerDetails && <SellerInformation />}
                        {renderKA(shouldRenderKA, productDetails, pdpProductType)}
                    </div>
                </div>
            </div>
            <div className={dt([...gridSpacing?.col1])} />
        </>
    );
};
const mapDispatchToProps = (dispatch) => ({
    actions: bindActionCreators(analyticsAction, dispatch),
});
export default connect(null, mapDispatchToProps)(DescriptionAndShipping);
